import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import {
  TokenResponse, User,
} from '../../../types/interfaces';
import { authApi } from '../../../api/services/auth.service';
import {
  clearRememberMeData,
  saveRememberMeData,
} from '../lib';
import { resetItems } from '../../product/model';
import { setErrorNotification, setNotification } from '../../../app/store/notifications';
import { resetCart } from '../../cart/model';
import { resetOrders } from '../../order/model';

export const checkEmailAvailable = createAsyncThunk(
  'auth/checkEmailAvailable',
  async (email: string) => {
    const response = await authApi.checkEmailAvailable(email);
    return response.data;
  }
);

export const getMe = createAsyncThunk('auth/me', async () => {
  const response = await authApi.getMe();
  return response.data;
});



export const updateMe = createAsyncThunk(
  'auth/updateMe',
  async ({ data }: { data: Partial<User> | FormData }, { dispatch }) => {
    try {
      const response = await authApi.updateMe(data);
      dispatch(setNotification('Данные пользователя успешно изменены.'));
      return response.data;
    } catch (error) {
      dispatch(
        setErrorNotification('Ошибка при изменении данных пользователя.')
      );
      throw error;
    }
  }
);

export const checkUsernameAvailable = createAsyncThunk(
  'auth/checkUsernameAvailable',
  async (username: string) => {
    const response = await authApi.checkUsernameAvailable(username);
    return response.data;
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (
    {
      username,
      password,
      rememberMe = false,
    }: {
      username: string;
      password: string;
      rememberMe?: boolean;
    },
    { rejectWithValue }
  ) => {
    try {
      const response: AxiosResponse<TokenResponse> = await authApi.login(
        username,
        password
      );
      // saveRememberMeData ниже уже пишет user в `_rememberMeUser_`
      // — отдельный `localStorage.setItem('user', ...)` тут был
      // дубликатом в мёртвый ключ, который никто не читает. Убрано.
      saveRememberMeData(response.data, rememberMe, 3600 * 24 * 7);
      return response.data;
    } catch (error: any) {
      // Раньше эту ошибку никто не ловил — axios прокидывал её
      // в `action.error.message`, и юзер видел в форме голое
      // «Request failed with status code 401». Теперь возвращаем
      // человекочитаемое сообщение через rejectWithValue: slice
      // запишет его в state.auth.error, и Login.tsx покажет в форме
      // нормальный hint.
      const status = error?.response?.status;
      const detail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.non_field_errors?.[0];
      // 400 и 401 — оба валидные «неверные данные»: SimpleJWT и DRF
      // обычно отдают 401, кастомные login-views иногда — 400.
      if (status === 400 || status === 401) {
        return rejectWithValue(
          detail || 'Неверный логин или пароль. Проверьте корректность ввода.'
        );
      }
      // 5xx / network — отдельный текст, чтобы юзер не пытался
      // править логин зря.
      if (!status) {
        return rejectWithValue(
          'Не удалось связаться с сервером. Проверьте интернет-соединение.'
        );
      }
      return rejectWithValue(
        detail || 'Не удалось войти. Попробуйте ещё раз позже.'
      );
    }
  }
);

export const logout = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue, dispatch } ) => {
    try {
      await authApi.logout();
    } catch (error: any) {
      return rejectWithValue(error.response.data);
    } finally {
      clearRememberMeData();
      // legacy `localStorage.removeItem('user')` убран — этот ключ
      // больше нигде не пишется (см. login-thunk и slice updateMe).
      dispatch(resetItems());
      dispatch(resetCart())
      dispatch(resetOrders())
    }
  }
);

export const requestResetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ email }: { email: string }) => {
    await authApi.passwordResetRequest({ email });
  }
);

export const resetPassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ code, new_password }: { code: string, new_password: string }) => {
    await authApi.resetPassword({ code, new_password });
  }
);

export const updatePassword = createAsyncThunk(
  'auth/resetPassword',
  async ({ old_password, new_password }: { old_password: string, new_password: string }, { dispatch }) => {
    try {
      await authApi.updatePassword({ old_password, new_password });
      dispatch(setNotification('Пароль успешно изменен.'));
    } catch (error) {
      dispatch(setErrorNotification('Ошибка при изменении пароля.'));
    }
  
  }
);

